"use client";

import type { Variants } from "framer-motion";
import { motion } from "framer-motion";
import React from "react";

import { useTranslation } from "@/hooks/useTranslation";
import type { FeedbackData } from "@/types";

type Props = {
  formData: FeedbackData;
  onInputChange?: (data: FeedbackData) => void;
  maxMessageLength?: number;
};

export default function FeedbackInfo({
  formData,
  onInputChange,
  maxMessageLength = 500,
}: Props) {
  const { t } = useTranslation();

  const itemVariants: Variants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut",
      },
    },
  };

  const messageLength = formData.messages?.length || 0;
  const isNearLimit = messageLength > maxMessageLength * 0.8;
  const isAtLimit = messageLength >= maxMessageLength;

  let textareaClass = "border-primary-500 focus:ring-primary-200";
  if (isAtLimit) {
    textareaClass = "border-red-500 focus:ring-red-200";
  } else if (isNearLimit) {
    textareaClass = "border-yellow-500 focus:ring-yellow-200";
  }

  let counterClass = "text-gray-500";
  if (isAtLimit) {
    counterClass = "text-red-500";
  } else if (isNearLimit) {
    counterClass = "text-yellow-600";
  }

  return (
    <motion.div initial="hidden" animate="visible" variants={itemVariants}>
      <div className="flex flex-col justify-center gap-5">
        {/* name */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <label
              htmlFor="name"
              className={`block font-medium text-gray-700 `}
            >
              {t({ de: "Nane", en: "Name" })}{" "}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              placeholder={t({ de: "Name", en: "Name" })}
              className={`w-full rounded-full border-2 border-primary-500 bg-primary-50 px-6 py-4 placeholder:text-primary-600 focus:outline-none focus:ring-2`}
              value={formData.name}
              onChange={(
                e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                const { value } = e.target;
                if (onInputChange) {
                  onInputChange({
                    ...formData,
                    name: value,
                  });
                }
              }}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label
              htmlFor="country"
              className={`block font-medium text-gray-700 `}
            >
              {t({ de: "Land", en: "Country" })}{" "}
              <span className="text-red-500">*</span>
            </label>
            <input
              id="country"
              name="country"
              type="text"
              required
              className={`w-full rounded-full border-2 border-primary-500 bg-primary-50 px-6 py-4 placeholder:text-primary-600 focus:outline-none focus:ring-2`}
              placeholder={t({
                de: "Land",
                en: "Country",
              })}
              value={formData.country || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const { value } = e.target;
                if (onInputChange) {
                  onInputChange({
                    ...formData,
                    country: value,
                  });
                }
              }}
            />
          </div>
        </div>

        {/* email & phone */}
        <div className="grid grid-cols-1 gap-5">
          <div className="flex flex-col gap-2">
            <label
              htmlFor="email"
              className={`block font-medium text-gray-700 `}
            >
              {t({ de: "Email", en: "Email" })}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder={t({ de: "Email", en: "Email" })}
              className={`w-full rounded-full border-2 border-primary-500 bg-primary-50 px-6 py-4 placeholder:text-primary-600 focus:outline-none focus:ring-2`}
              value={formData.email || ""}
              onChange={(
                e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                const { value } = e.target;
                if (onInputChange) {
                  onInputChange({
                    ...formData,
                    email: value,
                  });
                }
              }}
            />
          </div>
        </div>
        {/* messages with counter */}
        <div className="flex flex-col gap-2">
          <label
            htmlFor="messages"
            className="block font-medium text-gray-700 "
          >
            {t({ de: "Nachricht", en: "Messages" })}{" "}
            <span className="text-red-500">*</span>
          </label>
          <textarea
            id="messages"
            name="messages"
            rows={6}
            required
            maxLength={maxMessageLength}
            className={`w-full rounded-3xl border-2 bg-primary-50 px-6 py-4 placeholder:text-primary-600 focus:outline-none focus:ring-2 ${textareaClass}`}
            placeholder={t({
              de: "Ihre Nachricht hier...",
              en: "Your message here...",
            })}
            value={formData.messages || ""}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              const { value } = e.target;
              if (onInputChange) {
                onInputChange({ ...formData, messages: value });
              }
            }}
          />
          {/* Character counter & warning */}
          <div className="mt-1 flex items-center justify-between text-sm">
            <div>
              {isAtLimit && (
                <span className="font-medium text-red-500">
                  {t({
                    de: "Maximale Zeichenanzahl erreicht",
                    en: "Maximum character limit reached",
                  })}
                </span>
              )}
            </div>
            <div className={`font-medium ${counterClass}`}>
              {messageLength}/{maxMessageLength}{" "}
              {t({ de: "Zeichen", en: "characters" })}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
